import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInWithCustomToken, signInAnonymously } from 'firebase/auth';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';

const SpinWheel = () => {
  const [app, setApp] = useState(null);
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinResult, setSpinResult] = useState('');
  const [username, setUsername] = useState('');
  const [ticket, setTicket] = useState('');
  const [rotation, setRotation] = useState(0);
  const [rewards, setRewards] = useState([]);
  const [message, setMessage] = useState('Masukkan username & tiket untuk memutar roda!');
  const [showWinOverlay, setShowWinOverlay] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);

  // Firebase initialization and authentication
  useEffect(() => {
    const initializeFirebase = async () => {
      try {
        if (!process.env.REACT_APP_FIREBASE_CONFIG) {
          throw new Error('Firebase config is not defined in environment variables.');
        }
        const firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG);
        const firebaseApp = initializeApp(firebaseConfig);
        setApp(firebaseApp);
        const authInstance = getAuth(firebaseApp);

        if (process.env.REACT_APP_INITIAL_AUTH_TOKEN) {
            await signInWithCustomToken(authInstance, process.env.REACT_APP_INITIAL_AUTH_TOKEN);
        } else {
            await signInAnonymously(authInstance);
        }
        
        setDb(getFirestore(firebaseApp));
        setAuth(authInstance);
        setUserId(authInstance.currentUser?.uid || crypto.randomUUID());
      } catch (e) {
        console.error("Firebase initialization error:", e);
        setMessage("Failed to initialize the app. Please try again.");
      }
    };

    initializeFirebase();
  }, []);

  // Effect for fetching rewards data from Firestore in real-time
  useEffect(() => {
    if (!db || !userId) {
      return;
    }

    try {
      const appId = process.env.REACT_APP_ID;
      if (!appId) {
        console.error('Firebase app ID is not defined in environment variables.');
        setMessage('Firebase configuration is missing. App cannot load rewards.');
        return;
      }
      const rewardsCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/rewards`);
      
      const unsubscribe = onSnapshot(rewardsCollectionRef, (querySnapshot) => {
        const fetchedRewards = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRewards(fetchedRewards);
      });

      // Clean up the listener when the component unmounts
      return () => unsubscribe();
    } catch (e) {
      console.error("Error fetching reward data:", e);
      setMessage("Error fetching rewards. Please check the CMS Dashboard.");
    }
  }, [db, userId]);

  const handleSpin = async () => {
    if (isSpinning || !app) return;
    setIsSpinning(true);
    setSpinResult('');
    setMessage('');
    setShowWinOverlay(false);

    if (rewards.length === 0) {
        setMessage("Tidak ada hadiah yang tersedia!");
        setIsSpinning(false);
        return;
    }

    try {
        const functions = getFunctions(app);
        const spinWheelCallable = httpsCallable(functions, 'spinWheel');
        
        const result = await spinWheelCallable({
            username: username,
            ticket: ticket,
        });

        const finalRewardName = result.data.reward.name;
        const winningIndex = rewards.findIndex(reward => reward.name === finalRewardName);
        
        if (winningIndex === -1) {
            console.error("Winning reward not found in the list.");
            setMessage('Terjadi kesalahan saat memutar roda.');
            setIsSpinning(false);
            return;
        }

        const degreesPerSection = 360 / rewards.length;
        const extraSpins = 360 * 5;
        const newRotation = extraSpins - (degreesPerSection * winningIndex);

        setRotation(newRotation);
        setSpinResult(finalRewardName);

        setTimeout(() => {
            setIsSpinning(false);
            setMessage(result.data.message);
            if (finalRewardName.toLowerCase() !== 'zonk' && finalRewardName !== '') {
                setShowWinOverlay(true);
            }
        }, 5000);
        
    } catch (error) {
        console.error('Error calling Cloud Function:', error);
        setMessage('Terjadi kesalahan saat memutar roda.');
        setIsSpinning(false);
    }
  };

  const handleReset = () => {
    setRotation(0);
    setSpinResult('');
    setMessage('Roda siap untuk putaran berikutnya!');
    setShowWinOverlay(false);
  };

  const handleLogin = () => {
    alert('Fungsi Masuk belum diimplementasikan.');
  };

  const handleRegister = () => {
    alert('Fungsi Daftar belum diimplementasikan.');
  };

  const renderRewards = () => {
    if (rewards.length === 0) {
      return <text x="50" y="50" textAnchor="middle" fill="white" fontSize="4" dominantBaseline="middle">No rewards added</text>;
    }

    return rewards.map((reward, i) => {
      const degreesPerSection = 360 / rewards.length;
      const startAngle = degreesPerSection * i - 90;
      const endAngle = degreesPerSection * (i + 1) - 90;
      
      const toRadians = (degrees) => degrees * (Math.PI / 180);
      const startX = 50 + 50 * Math.cos(toRadians(startAngle));
      const startY = 50 + 50 * Math.sin(toRadians(startAngle));
      const endX = 50 + 50 * Math.cos(toRadians(endAngle));
      const endY = 50 + 50 * Math.sin(toRadians(endAngle));
      
      const color = reward.background || (i % 2 === 0 ? '#1B1B1B' : '#333333');
      const textColor = reward.color || '#FFFFFF';
      const text = reward.name;
      const textRotation = startAngle + degreesPerSection / 2 + 90;

      return (
        <g key={i}>
          <path
            d={`M50,50 L${startX},${startY} A50,50 0 ${degreesPerSection > 180 ? 1 : 0},1 ${endX},${endY} Z`}
            fill={color}
          />
          <text
            x="50"
            y="15"
            fill={textColor}
            textAnchor="middle"
            transform={`rotate(${textRotation} 50 50)`}
            fontSize="3"
            dominantBaseline="middle"
            style={{ fontWeight: 'bold' }}
          >
            {text}
          </text>
        </g>
      );
    });
  };

  const WinOverlay = ({ message, onDismiss }) => {
    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
            <div className="relative text-center text-white p-8 rounded-lg shadow-xl animate-scale-up-center" style={{ 
                background: 'radial-gradient(circle, rgba(255,100,100,1) 0%, rgba(200,0,0,1) 100%)',
                border: '4px solid gold'
            }}>
                <h3 className="text-4xl md:text-6xl font-black mb-4 animate-text-glow">BIG WIN!</h3>
                <p className="text-xl md:text-2xl mb-6">{message}</p>
                <button onClick={onDismiss} className="py-2 px-6 rounded-lg font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200">
                    OK
                </button>
            </div>
        </div>
    );
  };

  return (
    <div className="relative min-h-screen bg-[#0E1528] flex flex-col items-center p-4 lg:p-8 font-sans overflow-hidden text-white">
      {/* Background with abstract shapes */}
      <div className="absolute inset-0 z-0 opacity-10" style={{
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 100 100"><defs><radialGradient id="g1" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="%231E293B" /><stop offset="100%" stopColor="%230E1528" /></radialGradient></defs><rect width="100" height="100" fill="url(%23g1)" /><path d="M0,0 C20,30 40,10 50,20 S80,50 100,30 L100,0 Z" fill="%232D3748" opacity="0.5" /><path d="M0,100 C20,70 40,90 50,80 S80,50 100,70 L100,100 Z" fill="%232D3748" opacity="0.5" /></svg>')`,
          backgroundSize: 'cover'
      }}></div>

      {/* Header with navigation */}
      <header className="relative z-10 w-full flex justify-between items-center py-4 px-6 mb-8">
        <div className="flex items-center space-x-2">
          <svg width="40" height="40" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <defs><linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4A90E2" /><stop offset="100%" stopColor="#50B6E8" /></linearGradient></defs>
            <circle cx="50" cy="50" r="45" stroke="url(#logoGradient)" strokeWidth="5" fill="none" />
            <path d="M 40 30 Q 50 20 60 30 T 40 70 T 60 70 T 40 30" fill="url(#logoGradient)" stroke="url(#logoGradient)" strokeWidth="2" />
          </svg>
          <span className="font-bold text-2xl">Spin&Win</span>
        </div>
        <div className="flex space-x-4">
          <button onClick={handleLogin} className="py-2 px-6 rounded-full font-bold text-white bg-red-600 hover:bg-red-700 transition-colors duration-200 shadow-lg">MASUK</button>
          <button onClick={handleRegister} className="py-2 px-6 rounded-full font-bold text-white bg-orange-500 hover:bg-orange-600 transition-colors duration-200 shadow-lg">DAFTAR</button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="relative z-10 w-full max-w-screen-xl mx-auto flex flex-col lg:flex-row items-center justify-center space-y-8 lg:space-y-0 lg:space-x-12">
        {/* Left Side: Spin Wheel */}
        <div className="relative flex flex-col items-center justify-center w-full lg:w-1/2">
            <div className="text-xl md:text-2xl font-bold mb-4 hidden">Spin&Win</div>
            <div className="text-sm md:text-base text-gray-400 mb-8 hidden">― Swipe down or tap to play ―</div>
          <div className="relative w-full max-w-[500px] aspect-square">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <g id="wheel" style={{ transform: `rotate(${rotation}deg)`, transformOrigin: 'center', transition: isSpinning ? 'transform 6s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none' }}>
                {renderRewards()}
              </g>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                <span className="text-blue-600 font-bold text-base text-center leading-tight">Spin<br/>&<br/>Win</span>
              </div>
            </div>
            {/* Pointer, now facing down */}
            <div className="absolute w-12 h-12 top-0 left-1/2 -translate-x-1/2 -translate-y-full pointer bg-red-600" style={{clipPath: 'polygon(50% 100%, 0% 0%, 100% 0%)'}}></div>
          </div>
        </div>

        {/* Right Side: Spin Controls and Info */}
        <div className="w-full lg:w-1/2 p-8 bg-[#1B1B1B] rounded-xl shadow-lg border border-[#333] max-w-md">
          <h2 className="text-2xl font-bold mb-4 text-center text-white">Masukkan Username & Tiket</h2>
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col">
              <label htmlFor="username" className="font-semibold text-gray-400">Username</label>
              <input 
                type="text" 
                id="username" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                placeholder="Masukkan Username" 
                className="mt-1 p-3 bg-[#333] border border-[#444] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="ticket" className="font-semibold text-gray-400">Ticket</label>
              <input 
                type="text" 
                id="ticket" 
                value={ticket} 
                onChange={(e) => setTicket(e.target.value)} 
                placeholder="Masukkan Tiket" 
                className="mt-1 p-3 bg-[#333] border border-[#444] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              />
            </div>
            <button
              onClick={handleSpin}
              disabled={isSpinning || !username || !ticket || rewards.length === 0}
              className={`py-4 px-6 rounded-lg text-lg font-bold text-white transition-colors duration-200 shadow-md ${
                isSpinning
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 transform hover:scale-105'
              }`}
            >
              {isSpinning ? 'Memutar...' : 'SPIN'}
            </button>
            <button
              onClick={handleReset}
              className="py-4 px-6 rounded-lg text-lg font-bold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors duration-200 shadow-sm"
            >
              Reset
            </button>
            {message && (
              <div className="mt-6 p-4 bg-[#333] text-green-400 rounded-lg font-bold text-center border border-green-500 shadow-inner">
                {message}
              </div>
            )}
          </div>
        </div>
      </main>
      {showWinOverlay && (
        <WinOverlay message={`Selamat! Anda mendapatkan: ${spinResult}`} onDismiss={() => setShowWinOverlay(false)} />
      )}
    </div>
  );
};

export default SpinWheel;
